/**
 * Markdown processing utilities for RFP Analysis Result feature
 */

/**
 * Converts markdown text into structured blocks for the visual editor
 */
export const markdownToBlocks = (text) => {
    if (!text || typeof text !== 'string') return [];
    const rawLines = text.split('\n');
    const blocks = [];
    let blockIdx = 0;

    for (let i = 0; i < rawLines.length; i++) {
        const line = rawLines[i];
        const trimmed = line.trim();
        if (!trimmed) continue;

        // Check for Table
        if (trimmed.startsWith('|')) {
            const tableLines = [];
            let j = i;
            while (j < rawLines.length && (rawLines[j].trim().startsWith('|') || rawLines[j].trim() === '')) {
                if (rawLines[j].trim() !== '') {
                    tableLines.push(rawLines[j].trim());
                }
                j++;
            }
            if (tableLines.length > 0) {
                blocks.push({
                    id: `tb-${blockIdx++}`,
                    type: 'table',
                    content: tableLines.join('\n')
                });
                i = j - 1;
                continue;
            }
        }

        // Check for Headings
        const hashMatch = trimmed.match(/^(#{1,4})\s+(.+)$/);
        if (hashMatch) {
            const level = hashMatch[1].length;
            let type = 'h3';
            if (level === 1 || level === 2) type = 'h2';
            else if (level === 3) type = 'h3';
            else if (level >= 4) type = 'h4';
            blocks.push({ id: `h${level}-${blockIdx++}`, type, content: hashMatch[2] });
            continue;
        }

        // Check for Bold Heading
        if (trimmed.match(/^\*\*(.+?)\*\*\s*$/)) {
            blocks.push({ id: `bh-${blockIdx++}`, type: 'bold_heading', content: trimmed.match(/^\*\*(.+?)\*\*\s*$/)[1] });
            continue;
        }

        // Check for Bullets
        const boldBulletMatch = trimmed.match(/^[*-]\s+\*\*(.*?)\*\*(.*)/);
        if (boldBulletMatch) {
            blocks.push({ id: `bb-${blockIdx++}`, type: 'bold_bullet', boldPart: boldBulletMatch[1], content: boldBulletMatch[2].trim() });
            continue;
        }

        if (trimmed.match(/^[*-]\s+(.+)/)) {
            blocks.push({ id: `bu-${blockIdx++}`, type: 'bullet', content: trimmed.match(/^[*-]\s+(.+)/)[1] });
            continue;
        }

        // Check for Numbered List
        const numberedMatch = trimmed.match(/^(\d+)\.\s+(.+)/);
        if (numberedMatch) {
            blocks.push({ id: `nu-${blockIdx++}`, type: 'number', num: numberedMatch[1], content: numberedMatch[2] });
            continue;
        }

        // Check for full block bold/italic
        let content = line;
        let isBold = false;
        let isItalic = false;

        if (trimmed.startsWith('***') && trimmed.endsWith('***')) {
            isBold = true;
            isItalic = true;
            content = trimmed.slice(3, -3);
        } else if (trimmed.startsWith('**') && trimmed.endsWith('**')) {
            isBold = true;
            content = trimmed.slice(2, -2);
        } else if (trimmed.startsWith('*') && trimmed.endsWith('*')) {
            isItalic = true;
            content = trimmed.slice(1, -1);
        }

        blocks.push({ id: `tx-${blockIdx++}`, type: 'text', content, isBold, isItalic });
    }

    return blocks;
};

/**
 * Converts visual editor blocks back into markdown text
 */
export const blocksToMarkdown = (blocks) => {
    return blocks.map(block => {
        let content = block.content;
        if (block.isBold && block.isItalic) content = `***${content}***`;
        else if (block.isBold) content = `**${content}**`;
        else if (block.isItalic) content = `*${content}*`;

        switch (block.type) {
            case 'h2': return `# ${block.content}`;
            case 'h3': return `## ${block.content}`;
            case 'h4': return `#### ${block.content}`;
            case 'bold_heading': return `**${block.content}**`;
            case 'bold_bullet': return `* **${block.boldPart}** ${block.content}`.trimEnd();
            case 'bullet': return `* ${content}`;
            case 'number': return `${block.num}. ${content}`;
            case 'table': return block.content; // table content is already markdown
            default: return content;
        }
    }).join('\n');
};

/**
 * Parses markdown table strings into a structured object with headers and rows
 */
export const parseMarkdownTable = (tableLines) => {
    if (!tableLines || tableLines.length < 1) return { headers: [], data: [] };

    // Check if there is a separator line (|---|---|)
    const separatorRegex = /^\|[\s:|]*-[\s-:|]*\|$/;
    const separatorIdx = tableLines.findIndex(line => line.trim().match(separatorRegex));

    const parseRow = (row) => row
        .split('|')
        .map(c => c.trim())
        .filter((c, i, arr) => !(i === 0 && c === '') && !(i === arr.length - 1 && c === ''));

    if (separatorIdx !== -1) {
        // Standard markdown table with separator
        let headers = parseRow(tableLines[0]);
        let dataRows = tableLines.filter((line, idx) => {
            const t = line.trim();
            return t.startsWith('|') && idx !== 0 && idx !== separatorIdx;
        });

        // Detect if headers are generic Column 1, Column 2...
        const isFakeHeader = headers.length > 0 && headers.every((h, idx) => {
            const hl = h.toLowerCase().trim();
            return hl === `column ${idx + 1}` || hl === `column${idx + 1}`;
        });

        if (isFakeHeader && dataRows.length > 0) {
            headers = parseRow(dataRows[0]);
            dataRows = dataRows.slice(1);
        }

        const data = dataRows.map(row => {
            const cells = parseRow(row);
            const obj = {};
            headers.forEach((h, i) => { obj[h] = cells[i] || ''; });
            return obj;
        });

        return { headers, data };
    } else {
        // No separator found
        const dataRows = tableLines.filter(line => line.trim().startsWith('|'));
        if (dataRows.length === 0) return { headers: [], data: [] };

        let headers = parseRow(dataRows[0]);
        let remainingDataRows = dataRows.slice(1);

        // Detect if headers are generic Column 1, Column 2...
        const isFakeHeader = headers.length > 0 && headers.every((h, idx) => {
            const hl = h.toLowerCase().trim();
            return hl === `column ${idx + 1}` || hl === `column${idx + 1}`;
        });

        if (isFakeHeader && remainingDataRows.length > 0) {
            headers = parseRow(remainingDataRows[0]);
            remainingDataRows = remainingDataRows.slice(1);
        }

        const data = remainingDataRows.map(row => {
            const cells = parseRow(row);
            const obj = {};
            headers.forEach((h, i) => { obj[h] = cells[i] || ''; });
            return obj;
        });

        return { headers, data };
    }
};

/**
 * Converts structured table data back into markdown string
 */
export const blocksToMarkdownTable = (headers, data) => {
    if (!headers || headers.length === 0) return '';

    // Check if we have Column 1, Column 2 etc (generated headers)
    const isGenerated = headers.every(h => h.startsWith('Column '));

    const headerRow = `| ${headers.join(' | ')} |`;
    const separatorRow = `| ${headers.map(() => '---').join(' | ')} |`;
    const dataRows = data.map(row => {
        return `| ${headers.map(h => row[h] || '').join(' | ')} |`;
    });

    if (isGenerated) {
        // If they were generated, we don't want to save them as headers
        // just save the data rows
        return dataRows.join('\n');
    }

    return [headerRow, separatorRow, ...dataRows].join('\n');
};

/**
 * Converts inline markdown syntax to HTML (bold, italic, etc.)
 */
const formatInlineToHTML = (str) => {
    if (!str) return '';
    return str
        .replace(/\*\*\*(.+?)\*\*\*/g, '<strong><em>$1</em></strong>')
        .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
        .replace(/\*(.+?)\*/g, '<em>$1</em>')
        .replace(/_(.+?)_/g, '<em>$1</em>')
        .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank">$1</a>');
};

/**
 * Converts a raw markdown string directly to HTML for Tiptap.
 * This mirrors the logic of parseMarkdownString (view mode) to guarantee visual parity.
 */
export const markdownToHTML = (text) => {
    if (!text || typeof text !== 'string') return '<p></p>';

    const rawLines = text.split('\n');
    const parts = [];
    let i = 0;

    while (i < rawLines.length) {
        const line = rawLines[i].trim();

        if (!line) { i++; continue; }

        // ── Table ──────────────────────────────────────────────────────────
        if (line.startsWith('|')) {
            const tableLines = [];
            while (i < rawLines.length && rawLines[i].trim().startsWith('|')) {
                tableLines.push(rawLines[i].trim());
                i++;
            }
            const parsed = parseMarkdownTable(tableLines);
            if (parsed.headers && parsed.headers.length > 0) {
                const hdr = `<thead><tr>${parsed.headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`;
                const bdy = `<tbody>${parsed.data.map(row =>
                    `<tr>${parsed.headers.map(h => `<td>${row[h] || ''}</td>`).join('')}</tr>`
                ).join('')}</tbody>`;
                parts.push(`<table>${hdr}${bdy}</table>`);
            }
            continue;
        }

        // ── Headings ───────────────────────────────────────────────────────
        const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
        if (headingMatch) {
            const level = Math.min(headingMatch[1].length + 1, 4); // #→h2, ##→h3, ###→h4
            parts.push(`<h${level}>${formatInlineToHTML(headingMatch[2])}</h${level}>`);
            i++; continue;
        }

        // ── Bold heading (entire line is **text**) ─────────────────────────
        const boldHeadingMatch = line.match(/^\*\*(.+?)\*\*\s*$/);
        if (boldHeadingMatch) {
            parts.push(`<p><strong>${boldHeadingMatch[1]}</strong></p>`);
            i++; continue;
        }

        // ── Bullet list ────────────────────────────────────────────────────
        if (line.match(/^[*-]\s+.+/)) {
            const items = [];
            while (i < rawLines.length) {
                const l = rawLines[i].trim();
                const bm = l.match(/^[*-]\s+(.+)/);
                if (bm) { items.push(`<li>${formatInlineToHTML(bm[1])}</li>`); i++; }
                else break;
            }
            parts.push(`<ul>${items.join('')}</ul>`);
            continue;
        }

        // ── Numbered list ──────────────────────────────────────────────────
        if (line.match(/^\d+\.\s+.+/)) {
            const items = [];
            while (i < rawLines.length) {
                const nm = rawLines[i].trim().match(/^\d+\.\s+(.+)/);
                if (nm) { items.push(`<li>${formatInlineToHTML(nm[1])}</li>`); i++; }
                else break;
            }
            parts.push(`<ol>${items.join('')}</ol>`);
            continue;
        }

        // ── Default: paragraph ─────────────────────────────────────────────
        parts.push(`<p>${formatInlineToHTML(line)}</p>`);
        i++;
    }

    return parts.join('') || '<p></p>';
};

export const blocksToHTML = (blocks) => {
    if (!blocks || blocks.length === 0) return '';

    const parts = [];
    let i = 0;

    while (i < blocks.length) {
        const block = blocks[i];
        let content = block.content || '';

        // Inline formatting for non-table blocks
        if (block.type !== 'table' && content) {
            if (block.isBold && block.isItalic) content = `<strong><em>${content}</em></strong>`;
            else if (block.isBold) content = `<strong>${content}</strong>`;
            else if (block.isItalic) content = `<em>${content}</em>`;
        }

        if (block.type === 'h2') {
            parts.push(`<h2>${content}</h2>`);

        } else if (block.type === 'h3') {
            parts.push(`<h3>${content}</h3>`);

        } else if (block.type === 'h4') {
            parts.push(`<h4>${content}</h4>`);

        } else if (block.type === 'bold_heading') {
            parts.push(`<p><strong>${content}</strong></p>`);

        } else if (block.type === 'bullet' || block.type === 'bold_bullet') {
            // Collect consecutive bullet blocks into one <ul>
            const items = [];
            while (i < blocks.length && (blocks[i].type === 'bullet' || blocks[i].type === 'bold_bullet')) {
                const b = blocks[i];
                let bc = b.type === 'bold_bullet'
                    ? `<strong>${b.boldPart}</strong> ${b.content}`
                    : (b.content || '');
                items.push(`<li>${bc}</li>`);
                i++;
            }
            parts.push(`<ul>${items.join('')}</ul>`);
            continue; // i already incremented inside the while

        } else if (block.type === 'number') {
            // Collect consecutive numbered blocks into one <ol>
            const items = [];
            while (i < blocks.length && blocks[i].type === 'number') {
                items.push(`<li>${blocks[i].content || ''}</li>`);
                i++;
            }
            parts.push(`<ol>${items.join('')}</ol>`);
            continue;

        } else if (block.type === 'table') {
            // Fix: wrap in block scope to avoid switch-case const conflict
            const tableLines = (block.content || '').split('\n');
            const parsed = parseMarkdownTable(tableLines);

            if (parsed.headers && parsed.headers.length > 0) {
                const headerHtml = `<thead><tr>${parsed.headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>`;
                const bodyHtml = `<tbody>${parsed.data.map(row =>
                    `<tr>${parsed.headers.map(h => `<td>${row[h] || ''}</td>`).join('')}</tr>`
                ).join('')}</tbody>`;
                parts.push(`<table>${headerHtml}${bodyHtml}</table>`);
            } else {
                parts.push(`<p>${block.content}</p>`);
            }

        } else {
            // Default: plain paragraph
            parts.push(content ? `<p>${content}</p>` : '');
        }

        i++;
    }

    return parts.join('');
};

/**
 * Very basic HTML to Markdown converter for Phase 1 
 * (In a real app, we'd use a library like turndown)
 */
/**
 * Converts Tiptap HTML output back to Markdown for server storage.
 * Uses DOM parsing for reliability instead of fragile regex chains.
 * Heading levels: Tiptap h2 → # (main), h3 → ## (sub), h4 → ### (sub-sub)
 */
export const htmlToMarkdown = (html) => {
    if (!html) return '';

    // Parse in a temporary div to access the DOM
    const div = document.createElement('div');
    div.innerHTML = html;

    const processNode = (node) => {
        const tag = node.nodeName.toLowerCase();
        const children = Array.from(node.childNodes);

        // Text node
        if (node.nodeType === Node.TEXT_NODE) {
            return node.textContent || '';
        }

        // Inline formatting
        if (tag === 'strong' || tag === 'b') return `**${children.map(processNode).join('')}**`;
        if (tag === 'em' || tag === 'i') return `*${children.map(processNode).join('')}*`;
        if (tag === 'br') return '\n';
        if (tag === 'a') {
            const href = node.getAttribute('href') || '';
            return `<a href="${href}" target="_blank">${children.map(processNode).join('')}</a>`;
        }

        // Block elements
        const innerText = children.map(processNode).join('');

        if (tag === 'h2') return `# ${innerText}\n`;
        if (tag === 'h3') return `## ${innerText}\n`;
        if (tag === 'h4') return `### ${innerText}\n`;

        if (tag === 'p') {
            const text = innerText.trim();
            return text ? `${text}\n\n` : '';
        }

        if (tag === 'li') return innerText.trim();

        if (tag === 'ul') {
            return children
                .filter(c => c.nodeName.toLowerCase() === 'li')
                .map(li => `* ${processNode(li)}`)
                .join('\n') + '\n\n';
        }

        if (tag === 'ol') {
            return children
                .filter(c => c.nodeName.toLowerCase() === 'li')
                .map((li, idx) => `${idx + 1}. ${processNode(li)}`)
                .join('\n') + '\n\n';
        }

        if (tag === 'table') {
            const rows = [];
            node.querySelectorAll('tr').forEach((tr) => {
                const cells = Array.from(tr.querySelectorAll('th, td')).map(cell =>
                    cell.textContent?.trim() || ''
                );
                rows.push(`| ${cells.join(' | ')} |`);
            });

            if (rows.length > 0) {
                // Insert separator after header row
                const headerCellCount = node.querySelectorAll('tr')[0]
                    ? Array.from(node.querySelectorAll('tr')[0].querySelectorAll('th, td')).length
                    : 1;
                const separator = `| ${Array(headerCellCount).fill('---').join(' | ')} |`;
                rows.splice(1, 0, separator);
            }

            return '\n' + rows.join('\n') + '\n\n';
        }

        // Fallback: recurse into children
        return innerText;
    };

    const lines = Array.from(div.childNodes).map(processNode).join('');
    return lines.replace(/\n{3,}/g, '\n\n').trim();
};
